import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Eye, EyeOff, Mail, KeyRound, Lock } from "lucide-react";
import api, { apiError, passwordError } from "../lib/api";
import { BrandMark } from "../components/Layout";
import { useLang, LangToggle } from "../context/LanguageContext";

const inp = "w-full bg-[#141416] border border-zinc-800 text-white rounded-xl px-4 py-3.5 focus:border-[#F26722] focus:outline-none";
const btn = "w-full bg-[#F26722] hover:bg-[#D95513] disabled:opacity-60 text-white font-bold py-3.5 rounded-xl mt-4 flex items-center justify-center transition-colors";
const RESEND_WAIT = 60;

function useTT() { const { lang } = useLang(); return (en, nl) => (lang === "nl" ? nl : en); }

const NL_ERRORS = {
  "Invalid or expired reset code": "Ongeldige of verlopen herstelcode",
  "Too many attempts. Please request a new code.": "Te veel pogingen. Vraag een nieuwe code aan.",
  "Password must be at least 10 characters": "Wachtwoord moet minimaal 10 tekens hebben",
  "Password must include at least one letter": "Wachtwoord moet minimaal één letter bevatten",
  "Password must include at least one number": "Wachtwoord moet minimaal één cijfer bevatten",
};
const loc = (tt, m) => tt(m, NL_ERRORS[m] || m);

export default function ForgotPassword() {
  const nav = useNavigate();
  const tt = useTT();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState({ text: "", error: false });
  const [cooldown, setCooldown] = useState(0);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const sendCode = async () => {
    await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
    setCooldown(RESEND_WAIT);
  };
  const back = () => { setMsg({ text: "", error: false }); if (step === 1) nav("/login"); else setStep(step - 1); };
  const icons = [Mail, KeyRound, Lock];
  const StepIcon = icons[step - 1];

  return (
    <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto px-6 py-12" data-testid="forgot-page">
      <div className="flex items-center justify-between">
        <Link to="/" data-testid="forgot-logo-home"><BrandMark company={null} /></Link>
        <LangToggle />
      </div>
      <button onClick={back} data-testid="forgot-back-btn" className="flex items-center gap-2 text-zinc-400 hover:text-white mt-8 text-sm transition-colors"><ArrowLeft size={16} /> {step === 1 ? tt("Back to sign in", "Terug naar inloggen") : tt("Back", "Terug")}</button>
      <div className="mt-6 flex items-center justify-between">
        <span className="text-[11px] font-bold tracking-wider text-[#F26722] uppercase" data-testid="forgot-step-label">{tt(`Step ${step} of 3`, `Stap ${step} van 3`)}</span>
        <div className="flex gap-1.5">{[1, 2, 3].map((s) => <span key={s} className={`h-1.5 w-8 rounded-full transition-colors ${s <= step ? "bg-[#F26722]" : "bg-zinc-800"}`} />)}</div>
      </div>
      <div className="w-11 h-11 rounded-xl bg-[#F26722]/15 flex items-center justify-center mt-6"><StepIcon size={20} className="text-[#F26722]" /></div>
      {msg.text && <p data-testid="forgot-message" className={`text-sm mt-5 rounded-xl px-4 py-3 ${msg.error ? "bg-red-500/10 text-red-500" : "bg-emerald-500/10 text-emerald-600"}`}>{msg.text}</p>}
      <div key={step} className="fade-up">
        {step === 1 && <EmailStep tt={tt} email={email} setEmail={setEmail} sendCode={sendCode} onDone={() => { setCode(""); setLocked(false); setMsg({ text: tt("If that email exists, we sent a 6-digit code.", "Als dat e-mailadres bestaat, hebben we een 6-cijferige code gestuurd."), error: false }); setStep(2); }} />}
        {step === 2 && <CodeStep tt={tt} email={email.trim().toLowerCase()} code={code} setCode={setCode} setMsg={setMsg} sendCode={sendCode} cooldown={cooldown} locked={locked} setLocked={setLocked} onDone={() => { setMsg({ text: "", error: false }); setStep(3); }} />}
        {step === 3 && <PasswordStep tt={tt} email={email} code={code} setMsg={setMsg}
          onCodeProblem={(text, st) => { setCode(""); setLocked(st === 429); setMsg({ text: loc(tt, text), error: true }); setStep(2); }}
          onDone={() => { setCode(""); toast.success(tt("Password updated — please sign in", "Wachtwoord bijgewerkt — log opnieuw in")); nav("/login", { replace: true }); }} />}
      </div>
    </div>
  );
}

function EmailStep({ tt, email, setEmail, sendCode, onDone }) {
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await sendCode(); onDone(); } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit}>
      <h1 className="text-3xl font-extrabold text-white mt-4">{tt("Reset password", "Wachtwoord herstellen")}</h1>
      <p className="text-zinc-400 text-sm mt-1">{tt("Enter your email and we'll send you a 6-digit code.", "Vul je e-mailadres in en we sturen je een 6-cijferige code.")}</p>
      <input data-testid="forgot-email-input" type="email" required autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inp + " mt-6"} />
      <button data-testid="forgot-send-code-btn" disabled={loading} className={btn}>{loading ? <Loader2 className="animate-spin" size={20} /> : tt("Send code", "Code versturen")}</button>
    </form>
  );
}

function CodeStep({ tt, email, code, setCode, setMsg, sendCode, cooldown, locked, setLocked, onDone }) {
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/verify-reset-code", { email: email.trim().toLowerCase(), code }); onDone(); }
    catch (err) {
      const st = err.response?.status;
      if (st === 429) setLocked(true);
      setMsg({ text: loc(tt, apiError(err)), error: true });
    } finally { setLoading(false); }
  };
  const resend = async () => {
    try { await sendCode(); setLocked(false); setCode(""); setMsg({ text: tt("If that email exists, we sent a new 6-digit code.", "Als dat e-mailadres bestaat, hebben we een nieuwe 6-cijferige code gestuurd."), error: false }); }
    catch (err) { setMsg({ text: apiError(err), error: true }); }
  };
  return (
    <form onSubmit={submit}>
      <h1 className="text-3xl font-extrabold text-white mt-4">{tt("Enter the code", "Voer de code in")}</h1>
      <p className="text-zinc-400 text-sm mt-1" data-testid="forgot-code-text">{tt(`We sent a 6-digit code to ${email}. Enter it below.`, `We hebben een 6-cijferige code gestuurd naar ${email}. Vul hem hieronder in.`)}</p>
      <input data-testid="forgot-code-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required autoFocus disabled={locked}
        value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••"
        className={inp + " mt-6 text-center text-2xl tracking-[0.5em] font-mono disabled:opacity-50"} />
      <p className="text-xs text-zinc-500 mt-2">{tt("The code is valid for 15 minutes. After 5 wrong attempts you need a new code.", "De code is 15 minuten geldig. Na 5 foute pogingen heb je een nieuwe code nodig.")}</p>
      {locked
        ? <button type="button" onClick={resend} disabled={cooldown > 0} data-testid="forgot-resend-primary-btn" className={btn}>{cooldown > 0 ? tt(`Resend code (${cooldown}s)`, `Code opnieuw sturen (${cooldown}s)`) : tt("Resend code", "Code opnieuw sturen")}</button>
        : <button data-testid="forgot-verify-btn" disabled={loading || code.length !== 6} className={btn}>{loading ? <Loader2 className="animate-spin" size={20} /> : tt("Verify code", "Code controleren")}</button>}
      {!locked && (
        <p className="text-sm text-zinc-400 mt-5 text-center">{tt("Didn't get it?", "Niet ontvangen?")}{" "}
          <button type="button" onClick={resend} disabled={cooldown > 0} data-testid="forgot-resend-link" className="font-bold text-[#F26722] disabled:text-zinc-500 disabled:cursor-not-allowed">
            {cooldown > 0 ? tt(`Resend code in ${cooldown}s`, `Opnieuw sturen over ${cooldown}s`) : tt("Resend code", "Code opnieuw sturen")}
          </button>
        </p>
      )}
    </form>
  );
}

function PasswordStep({ tt, email, code, setMsg, onCodeProblem, onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setMsg({ text: tt("The passwords don't match.", "De wachtwoorden komen niet overeen."), error: true });
    const pe = passwordError(pw); if (pe) return setMsg({ text: loc(tt, pe), error: true });
    setLoading(true);
    try { await api.post("/auth/reset-password", { email: email.trim().toLowerCase(), code, new_password: pw }); onDone(); }
    catch (err) {
      const st = err.response?.status;
      if (st === 400 || st === 429) onCodeProblem(apiError(err), st);
      else setMsg({ text: loc(tt, apiError(err)), error: true });
    } finally { setLoading(false); }
  };
  const Toggle = () => <button type="button" onClick={() => setShow(!show)} data-testid="forgot-toggle-password" className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400" aria-label={show ? "Hide password" : "Show password"}>{show ? <EyeOff size={20} /> : <Eye size={20} />}</button>;
  return (
    <form onSubmit={submit}>
      <h1 className="text-3xl font-extrabold text-white mt-4">{tt("Choose a new password", "Kies een nieuw wachtwoord")}</h1>
      <p className="text-zinc-400 text-sm mt-1">{tt("At least 10 characters, with letters and numbers.", "Minimaal 10 tekens, met letters en cijfers.")}</p>
      <label className="block text-xs font-bold tracking-wider text-zinc-400 uppercase mt-6 mb-2">{tt("New password", "Nieuw wachtwoord")}</label>
      <div className="relative"><input data-testid="forgot-new-password-input" type={show ? "text" : "password"} autoComplete="new-password" required autoFocus value={pw} onChange={(e) => setPw(e.target.value)} className={inp + " pr-12"} /><Toggle /></div>
      <label className="block text-xs font-bold tracking-wider text-zinc-400 uppercase mt-4 mb-2">{tt("Confirm new password", "Bevestig nieuw wachtwoord")}</label>
      <div className="relative"><input data-testid="forgot-confirm-password-input" type={show ? "text" : "password"} autoComplete="new-password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className={inp + " pr-12"} /></div>
      <button data-testid="forgot-reset-btn" disabled={loading} className={btn}>{loading ? <Loader2 className="animate-spin" size={20} /> : tt("Update password", "Wachtwoord bijwerken")}</button>
    </form>
  );
}
